'use client'
import { createContext, useEffect, useMemo, useState } from 'react'
import type { FC, JSX } from 'react'
import { LANG } from '@/utils'
import { useParams } from 'next/navigation'
import { getDictionary } from '@/app/dictionaries'
import { LANGUAGES, type JsonPossibleValues, type TypeLang } from '@/schema'

type Dictionary = JsonPossibleValues
const DEFAULT_LANG = (LANG || 'es') as TypeLang

// Load initial dictionary synchronously
const loadInitialDictionary = (lang: TypeLang) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const base = require(`@/app/dictionaries/base/${lang}.json`)
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const project = require(`@/app/dictionaries/project/${lang}.json`)
  return { ...base, ...project }
}

// paramLang is undefined at this point since we're outside the component
const DEFAULT_DICTIONARY = loadInitialDictionary(DEFAULT_LANG)

export const TranslationsContext = createContext<{ dictionary: Dictionary }>({ dictionary: DEFAULT_DICTIONARY })

type Props = {
  children: JSX.Element
  paramLang?: TypeLang
}

let prevLang = DEFAULT_LANG
export const TranslationsProvider: FC<Props> = ({ children, paramLang }) => {
  const { lang } = useParams() as { lang: TypeLang }
  const initialLang = paramLang || DEFAULT_LANG
  const [dictionary, setDictionary] = useState<Dictionary>(() => {
    // Load dictionary with paramLang if provided
    return (initialLang !== DEFAULT_LANG && LANGUAGES.includes(initialLang)) ? loadInitialDictionary(initialLang) : DEFAULT_DICTIONARY
  })
  const currentLang = useMemo(() => lang ?? initialLang, [lang, initialLang])

  const contextValue = useMemo(() => ({ dictionary }), [dictionary])

  useEffect(() => {
    async function updateDictionary(lang: TypeLang) {
      const incomingDictionary = await getDictionary(lang)
      setDictionary(incomingDictionary)
    }

    if (lang && lang !== prevLang && lang !== initialLang) {
      updateDictionary(lang)
      prevLang = lang
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang, initialLang])

  return (
    <TranslationsContext.Provider value={contextValue}>
      {children}
    </TranslationsContext.Provider>
  )
}
